import React, { useEffect, useRef, useState } from "react";
import 'react-modern-drawer/dist/index.css';
import Chart from "chart.js/auto";
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { fmesh } from '../../../core/globalVariables.js';

const years = [
    "2024-01-01T00:00:00+00:00",
    "2027-01-01T00:00:00+00:00",
    "2029-01-01T00:00:00+00:00",
    "2031-01-01T00:00:00+00:00"
];

const PDFComparePanel = ({
    longstandingGeoJSONRef,
    selectedMetric,
    selectedYearEditing,
    universalParcelDifferentiator,
    landKeySelectedKey,
    comparingLandtypeSelectedOption,
    originalGeoJSONforComparingRef,
    isVisible,
    PDFCompareRef,
    isCreatingPDF
}) => {
    const PDFcurrentChartRef = useRef(null);
    const yearlyChartRef = useRef(null);
    const [logScale, setLogScale] = useState(false);

    const initChart = (chartRef, chartId, datasets) => {
        if (chartRef.current) {
            chartRef.current.destroy();
        }
        const ctx = document.getElementById(chartId);
        if (ctx) {
            chartRef.current = new Chart(ctx, {
                type: 'bar',
                data: {
                    labels: datasets.labels,
                    datasets: datasets.datasets
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        title: { display: false },
                        legend: { display: false }
                    },
                    scales: {
                        y: {
                            beginAtZero: true,
                            type: 'linear', // Add logarithmic scaling
                            ticks: {
                                callback: function (value) {
                                    if (logScale) {
                                        return Number(value.toString());
                                    }
                                    return value;
                                }
                            }
                        },
                        x: {
                            display: true,
                            stacked: false,
                            grid: { display: false },
                            ticks: { autoSkip: false, minRotation: 90, maxRotation: 90 },
                            barPercentage: 0.4,
                            categoryPercentage: 0.5
                        }
                    }
                }
            });
        }
    };

    const initChartLine = (lineChartRef, chartId, datasets) => {
        if (lineChartRef.current) {
            lineChartRef.current.destroy();
        }
        const ctx = document.getElementById(chartId);
        if (ctx) {
            lineChartRef.current = new Chart(ctx, {
                type: 'line',
                data: {
                    labels: datasets.labels,
                    datasets: datasets.datasets
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        title: { display: false },
                        legend: { display: true }
                    },
                    scales: {
                        y: { beginAtZero: true },
                        x: {
                            display: true,
                            stacked: false,
                            grid: { display: false },
                            ticks: { autoSkip: false, minRotation: 90, maxRotation: 90 }
                        }
                    }
                }
            });
        }
    };

    const parseCumulativeDataByKey = (geojson, selectedYearEditing, compareKey) => {
        const cumulativeDataMap = new Map();
        geojson.features.forEach(feature => {
            const keyValue = feature.feature_data_simple_timeseries[compareKey]?.[selectedYearEditing];
            const metricValue = feature.feature_data_simple_timeseries[selectedMetric]?.[selectedYearEditing] || 0;
            if (!keyValue) return;
            cumulativeDataMap.set(keyValue, (cumulativeDataMap.get(keyValue) || 0) + metricValue);
        });
        const sortedData = Array.from(cumulativeDataMap.entries()).sort((a, b) => b[1] - a[1]);
        return {
            labels: sortedData.map(([key]) => key),
            data: sortedData.map(([, value]) => value)
        };
    };

    const parseYearlyAccumulatedData = (geojson, compareKey) => {
        const yearlyAccumilativeDefiner = comparingLandtypeSelectedOption;
        const accumulatedData = new Map();
        const firstFeatureWithTimeseries = geojson.features.find(feature =>
            feature.feature_data_simple_timeseries &&
            (feature.feature_data_simple_timeseries[compareKey] || feature.feature_data_simple_timeseries[selectedMetric])
        );
        const dynamicYears = firstFeatureWithTimeseries
            ? Object.keys(firstFeatureWithTimeseries.feature_data_simple_timeseries[compareKey] || firstFeatureWithTimeseries.feature_data_simple_timeseries[selectedMetric])
            : [];
        dynamicYears.forEach(year => {
            geojson.features.forEach(feature => {
                const keyValue = feature.feature_data_simple_timeseries[compareKey]?.[year];
                const metricValue = feature.feature_data_simple_timeseries[selectedMetric]?.[year] || 0;
                if (
                    yearlyAccumilativeDefiner === "" ||
                    keyValue === yearlyAccumilativeDefiner ||
                    yearlyAccumilativeDefiner === "none"
                ) {
                    if (!accumulatedData.has(year)) {
                        accumulatedData.set(year, 0);
                    }
                    accumulatedData.set(year, accumulatedData.get(year) + metricValue);
                }
            });
        });
        const labels = dynamicYears;
        const datasets = [{
            label: `Accumulated Data by ${compareKey} per Year`,
            data: labels.map(year => accumulatedData.get(year) || 0),
        }];
        return { labels, datasets };
    };


    useEffect(() => {
        if (longstandingGeoJSONRef.current && landKeySelectedKey) {
            const yearlyAccumulatedData = parseYearlyAccumulatedData(longstandingGeoJSONRef.current, landKeySelectedKey);
            const originalYearlyAccumulatedData = parseYearlyAccumulatedData(originalGeoJSONforComparingRef.current, landKeySelectedKey);
            initChartLine(yearlyChartRef, 'yearlyChart', {
                labels: yearlyAccumulatedData.labels,
                datasets: [
                    {
                        label: `Longstanding Data by ${landKeySelectedKey}`,
                        data: yearlyAccumulatedData.datasets[0].data,
                        borderColor: '#36A2EB',
                        backgroundColor: 'rgba(54, 162, 235, 0.2)',
                        fill: true
                    },
                    {
                        label: `Original Data by ${landKeySelectedKey}`,
                        data: originalYearlyAccumulatedData.datasets[0].data,
                        borderColor: '#FF6384',
                        backgroundColor: 'rgba(255, 99, 132, 0.2)',
                        fill: true
                    }
                ]
            });
        }
    }, [comparingLandtypeSelectedOption, isCreatingPDF]);

    useEffect(() => {
        const geojson = longstandingGeoJSONRef.current;
        const originalGeojson = originalGeoJSONforComparingRef.current;
        console.log("PDF Compare Chart starting");
        if (geojson && originalGeojson) {

            console.log("PDF Compare Chart starting 2");

            const timeSeriesArray = [];

            fmesh.traverse((child) => {
                if (child.isMesh) {
                    for (let i = 0; i < geojson.features.length; i++) {
                        if (child.name === geojson.features[i].skylark_id) {
                            const timeSeriesData = geojson.features[i].feature_data_simple_timeseries[landKeySelectedKey]?.[selectedYearEditing];
                            if (timeSeriesData) {
                                const r = Math.round(child.material.color.r * 255);
                                const g = Math.round(child.material.color.g * 255);
                                const b = Math.round(child.material.color.b * 255);
                                const hexColor = `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()}`;
                                const uniqueEntry = { timeSeriesData, color: hexColor };

                                if (!timeSeriesArray.some(entry => entry.timeSeriesData === uniqueEntry.timeSeriesData && entry.color === uniqueEntry.color)) {
                                    timeSeriesArray.push(uniqueEntry);
                                }
                            }
                        }
                    }
                }
            });

            const cumulativeDataLongstanding = parseCumulativeDataByKey(geojson, selectedYearEditing, landKeySelectedKey);
            const cumulativeDataOriginal = parseCumulativeDataByKey(originalGeojson, selectedYearEditing, landKeySelectedKey);
            const barColorsLongstanding = cumulativeDataLongstanding.labels.map(label => {
                const matchingEntry = timeSeriesArray.find(entry => entry.timeSeriesData === label);
                return matchingEntry ? matchingEntry.color : '#36A2EB';
            });
            const barColorsOriginal = cumulativeDataOriginal.labels.map(label => {
                const matchingEntry = timeSeriesArray.find(entry => entry.timeSeriesData === label);
                return matchingEntry ? matchingEntry.color : '#FF6384';
            });

            initChart(PDFcurrentChartRef, 'PDFcurrentChart', {
                labels: cumulativeDataLongstanding.labels,
                datasets: [
                    {
                        label: `Longstanding Data by ${landKeySelectedKey}`,
                        data: cumulativeDataLongstanding.data,
                        backgroundColor: cumulativeDataLongstanding.labels.map(label => {
                            const matchingEntry = timeSeriesArray.find(entry => entry.timeSeriesData === label);
                            const color = matchingEntry ? matchingEntry.color : '#36A2EB';
                            return comparingLandtypeSelectedOption === "none" ? color : (comparingLandtypeSelectedOption === label ? color : `${color}80`); // 80 is the hex for 50% opacity
                        })
                    },
                    {
                        label: `Original Data by ${landKeySelectedKey}`,
                        data: cumulativeDataOriginal.data,
                        backgroundColor: cumulativeDataLongstanding.labels.map(label => {
                            const matchingEntry = timeSeriesArray.find(entry => entry.timeSeriesData === label);
                            const color = matchingEntry ? matchingEntry.color : '#36A2EB';
                            return comparingLandtypeSelectedOption === "none" ? color : (comparingLandtypeSelectedOption === label ? color : `${color}80`); // 80 is the hex for 50% opacity
                        })
                    }
                ]
            });

            let comparingLandtypeSelectedOptionColor;

            for (let i = 0; i < timeSeriesArray.length; i++) {
                if (timeSeriesArray[i].timeSeriesData === comparingLandtypeSelectedOption) {
                    comparingLandtypeSelectedOptionColor = timeSeriesArray[i].color;
                }
            }

            const yearlyAccumulatedData = parseYearlyAccumulatedData(geojson, landKeySelectedKey);
            const originalYearlyAccumulatedData = parseYearlyAccumulatedData(originalGeojson, landKeySelectedKey);
            initChartLine(yearlyChartRef, 'yearlyChart', {
                labels: yearlyAccumulatedData.labels,
                datasets: [
                    {
                        label: `Updated Data by ${landKeySelectedKey}`,
                        data: yearlyAccumulatedData.datasets[0].data,
                        borderColor: '#006400', // Dark Green
                        backgroundColor: 'rgba(0, 100, 0, 0.2)', // Dark Green with 20% opacity
                        fill: true
                    },
                    {
                        label: `Original Data by ${landKeySelectedKey}`,
                        data: originalYearlyAccumulatedData.datasets[0].data,
                        borderColor: '#800080', // Purple
                        backgroundColor: 'rgba(128, 0, 128, 0.2)', // Purple with 20% opacity
                        fill: true
                    }
                ]
            }, landKeySelectedKey, comparingLandtypeSelectedOptionColor);
        }
    }, [longstandingGeoJSONRef.current, originalGeoJSONforComparingRef.current, landKeySelectedKey, selectedYearEditing, selectedMetric, comparingLandtypeSelectedOption, logScale, isCreatingPDF]);

    return (
        <div style={{
            left: '400px',
            top: '0',
            height: 'auto',
            width: '1900px',
            zIndex: '10000',
            position: 'absolute',
            padding: '40px',
            paddingTop: '80px',
        }}
            ref={PDFCompareRef} >
            <div
                style={{
                    borderRadius: '5px',
                    padding: '30px',
                    display: 'inline-block',
                    paddingBottom: '50px',
                    height: 'auto',
                    width: '650px',
                }}
            >
                <div style={{
                    width: '100%',
                    height: '400px',
                    display: 'inline-block'
                }}>
                    <p>Cumulative Data by {landKeySelectedKey} for {selectedYearEditing.split('-')[0]}</p>
                    <canvas id="PDFcurrentChart"></canvas>
                </div>
            </div>
            <div
                style={{
                    borderRadius: '5px',
                    padding: '30px',
                    display: 'inline-block',
                    maxWidth: '1500px',
                    paddingBottom: '50px',
                    height: 'auto',
                    width: '650px',
                }}
            >
                <div style={{
                    width: '100%',
                    height: '400px',
                    display: 'inline-block'
                }}>
                    <p>Accumulative Data by {landKeySelectedKey} across Timeseries</p>
                    <canvas id="yearlyChart"></canvas>
                </div>
            </div>
        </div >
    );
};

export default PDFComparePanel;